import API, { graphqlOperation } from "@aws-amplify/api"
import {
	listNodeLocationsByLayoutId as listNodeLocationsByLayoutIdGql,
	listUserLayouts as listUserLayoutsGql,
	listMasterLocations as listMasterLocationsGql,
	getNodeDataByMLocId,
	getLatestNodeDataByMLocId,
	listLatestMasterDataByLayoutId,
	listReplacementCampaignInfoByLayoutId as listReplacementCampaignInfoGql,
	listMasterDataHistory,
} from "graphql/queries"
import {
	subscribeReplacementCampaignUpdate as subscribeReplacementCampaignUpdateGql,
	subscribeNodeIdUpdate as subscribeNodeIdUpdateGql,
	subscribeNodeSyncBitmasksUpdate as subscribeNodeSyncBitmasksUpdateGql,
	subscribeMasterIdUpdate as subscribeMasterIdUpdateGql,
} from "graphql/subscriptions"
import * as actionTypes from "./types"
import offlineStorage from "offlineStorage"

const cacheLoaded = {
	layoutDetails: false,
	masterDetails: {},
	nodeDetails: {},
}

const listToDict = (keyId, valuesList) => {
	if (valuesList === null) {
		throw new Error("valuesList cannot be null")
	}
	const valuesDict = {}
	for (let i = 0; i < valuesList.length; i++) {
		const thisValue = valuesList[i]
		if (thisValue === null) {
			throw new Error("value cannot be null")
		}
		const keyIdValue = thisValue[keyId]
		if (keyIdValue === undefined) {
			throw new Error(`Key '${keyId}' is not present in object #${i}`)
		}
		valuesDict[thisValue[keyId]] = thisValue
	}
	return valuesDict
}

const fetchCachedLayoutDetails = async () => {
	if (!cacheLoaded["layoutDetails"]) {
		cacheLoaded["layoutDetails"] = true
		return (await offlineStorage).getAll("layoutDetails")
	}
	return []
}

const fetchCachedMasterDetails = async (layoutId) => {
	if (!cacheLoaded["masterDetails"][layoutId]) {
		cacheLoaded["masterDetails"][layoutId] = true
		return (await offlineStorage).getAllFromIndex("masterDetails", "layoutId", layoutId)
	}
	return []
}

const fetchCachedNodeDetails = async (layoutId) => {
	if (!cacheLoaded["nodeDetails"][layoutId]) {
		cacheLoaded["nodeDetails"][layoutId] = true
		return (await offlineStorage).getAllFromIndex("nodeDetails", "layoutId", layoutId)
	}
	return []
}

const setCachedDetails = async (storeName, items) => {
	const tx = (await offlineStorage).transaction(storeName, "readwrite")
	const store = tx.objectStore(storeName)
	store.clear()
	for (let i = 0; i < items.length; i++) {
		store.put(items[i])
	}
	return tx.done
}

const fetchNetworkLayoutDetails = async () => {
	let data
	try {
		const result = await API.graphql(graphqlOperation(listUserLayoutsGql))
		data = result.data
	} catch (err) {
		data = err.data
		console.error("listUserLayouts", err)
	}
	if (data) {
		const { listUserLayouts } = data
		if (listUserLayouts) {
			const { items } = listUserLayouts
			if (items) {
				return items
			}
		}
	}
	return []
}

const fetchNetworkMasterDetails = async (layoutId) => {
	let data
	try {
		const result = await API.graphql(
			graphqlOperation(listMasterLocationsGql, {
				layoutId,
			}),
		)
		data = result.data
	} catch (err) {
		data = err.data
		console.warn("Occurred while getting saved master details (will try to remedy):", err)
	}
	if (data) {
		const { listMasterLocations } = data
		if (listMasterLocations) {
			const { items } = listMasterLocations
			if (items) {
				console.info("Successfully retrieved saved master details:", items)
				return items
			}
		}
	}
	console.error("Could not get saved master data.")
	return []
}

const fetchNetworkNodeDetails = async (layoutId) => {
	let data
	try {
		const result = await API.graphql(
			graphqlOperation(listNodeLocationsByLayoutIdGql, {
				layoutId,
			}),
		)
		data = result.data
	} catch (err) {
		data = err.data
		console.error("listNodeLocationsByLayoutId", err)
	}
	if (data) {
		const { listNodeLocationsByLayoutId } = data
		if (listNodeLocationsByLayoutId) {
			const { items } = listNodeLocationsByLayoutId
			if (items) {
				return items
			}
		}
	}
	return []
}

export const fetchLayoutDetails = () => async (dispatch, _getState) => {
	let layoutDetails

	const cachedResults = await fetchCachedLayoutDetails()
	if (cachedResults.length) {
		layoutDetails = listToDict("id", cachedResults)
		await dispatch({
			type: actionTypes.SET_LAYOUT_DETAILS,
			layoutDetails,
			fromCache: true,
		})
	}

	const networkResults = await fetchNetworkLayoutDetails()
	if (networkResults.length) {
		layoutDetails = listToDict("id", networkResults)
		await dispatch({
			type: actionTypes.SET_LAYOUT_DETAILS,
			layoutDetails,
			fromCache: false,
		})
		return await setCachedDetails("layoutDetails", networkResults)
	}
}

export const fetchMasterDetails = (layoutId) => async (dispatch) => {
	let masterDetails

	let cachedResults = await fetchCachedMasterDetails(layoutId)
	cachedResults = cachedResults.filter((n) => n) // Remove falsy/null values

	if (cachedResults.length) {
		console.info("Cached results:", cachedResults)
		masterDetails = listToDict("id", cachedResults)
		await dispatch({
			type: actionTypes.SET_MASTER_DETAILS,
			masterDetails,
			fromCache: true,
		})
	}

	let networkResults = await fetchNetworkMasterDetails(layoutId)
	networkResults = networkResults.filter((n) => n !== null && n !== undefined) // Remove null values

	if (networkResults.length) {
		console.info("Saved results:", networkResults)
		masterDetails = listToDict("id", networkResults)
		await dispatch({
			type: actionTypes.SET_MASTER_DETAILS,
			masterDetails,
			fromCache: false,
		})
		return await setCachedDetails("masterDetails", networkResults)
	}
}

export const fetchNodeDetails = (layoutId) => async (dispatch) => {
	const cachedResults = await fetchCachedNodeDetails(layoutId)
	if (cachedResults.length) {
		await dispatch({
			type: actionTypes.UPDATE_NODE_DETAILS,
			nodeDetails: cachedResults,
			fromCache: true,
		})
	}

	const networkResults = await fetchNetworkNodeDetails(layoutId)
	if (networkResults.length) {
		await dispatch({
			type: actionTypes.UPDATE_NODE_DETAILS,
			nodeDetails: networkResults,
			fromCache: false,
		})
		return await setCachedDetails("nodeDetails", networkResults)
	}
}

export const fetchNodeData = (layoutId, mLocId, userPrivileges, deviceType, timestamp) => async (dispatch) => {
	await dispatch({
		type: actionTypes.SET_NODE_DATA_STATUS,
		mLocId,
		status: "pending",
	})
	let data
	try {
		let result
		if (timestamp) {
			result = await API.graphql(
				graphqlOperation(getNodeDataByMLocId, {
					layoutId,
					mLocId,
					timestamp,
				}),
			)
		} else {
			result = await API.graphql(
				graphqlOperation(getLatestNodeDataByMLocId, {
					layoutId,
					mLocId,
				}),
			)
		}
		data = result.data
	} catch (err) {
		console.error("geNodeDataByMLocId", err)
		await dispatch({
			type: actionTypes.SET_NODE_DATA_STATUS,
			mLocId,
			status: "error",
		})
		if (err.errors.filter((e) => e.message === "Network Error").length > 0) {
			return "networkError"
		} else {
			return "error"
		}
	}

	if (data && (data.getLatestNodeDataByMLocId || data.getNodeDataByMLocId)) {
		const item = data.getLatestNodeDataByMLocId || data.getNodeDataByMLocId
		if (item !== undefined) {
			await dispatch({
				type: actionTypes.UPDATE_LATEST_NODE_DATA,
				data: item,
				userPrivileges,
				deviceType,
			})
			return "resolved"
		} else {
			await dispatch({
				type: actionTypes.SET_NODE_DATA_STATUS,
				mLocId,
				status: "resolved",
			})
			return "empty"
		}
	} else {
		await dispatch({
			type: actionTypes.SET_NODE_DATA_STATUS,
			mLocId,
			status: "resolved",
		})
		return "empty"
	}
}

export const fetchMasterDataByLayoutId = (layoutId, userPrivileges, deviceType, timestamp) => async (dispatch) => {
	await dispatch({
		type: actionTypes.SET_MASTER_DATA_STATUS,
		status: "pending",
	})
	let data
	try {
		let result
		if (timestamp) {
			result = await API.graphql(
				graphqlOperation(listMasterDataHistory, {
					layoutId,
					timestamp,
				}),
			)
		} else {
			result = await API.graphql(
				graphqlOperation(listLatestMasterDataByLayoutId, {
					layoutId,
				}),
			)
		}
		data = result.data
	} catch (err) {
		data = err.data
		console.error("listLatestMasterDataByLayoutId", err)
		await dispatch({
			type: actionTypes.SET_MASTER_DATA_STATUS,
			status: "error",
		})
		if (err.errors.filter((e) => e.message === "Network Error").length > 0) {
			return "networkError"
		} else {
			return "error"
		}
	}

	if (data && (data.listLatestMasterDataByLayoutId || data.listMasterDataHistory)) {
		const { items } = data.listLatestMasterDataByLayoutId || data.listMasterDataHistory
		if (items !== undefined) {
			await dispatch({
				type: actionTypes.UPDATE_LATEST_MASTER_DATA,
				data: items,
				userPrivileges,
				deviceType,
			})
			return "resolved"
		} else {
			await dispatch({
				type: actionTypes.SET_MASTER_DATA_STATUS,
				status: "resolved",
			})
			return "empty"
		}
	} else {
		await dispatch({
			type: actionTypes.SET_MASTER_DATA_STATUS,
			status: "resolved",
		})
		return "empty"
	}
}

export const listReplacementCampaignInfo = (layoutId) => async (dispatch) => {
	await dispatch({
		type: actionTypes.SET_REPLACEMENT_CAMPAIGN_STATUS,
		status: "pending",
	})
	let data
	try {
		const result = await API.graphql(
			graphqlOperation(listReplacementCampaignInfoGql, {
				layoutId,
			}),
		)
		data = result.data
	} catch (err) {
		data = err.data
		console.log("listReplacementCampaignInfo", err)
		await dispatch({
			type: actionTypes.SET_REPLACEMENT_CAMPAIGN_STATUS,
			status: "error",
		})
		if (err.errors.filter((e) => e.message === "Network Error").length > 0) {
			return "networkError"
		} else {
			return "error"
		}
	}

	if (data && data.listReplacementCampaignInfoByLayoutId) {
		const { items } = data.listReplacementCampaignInfoByLayoutId
		if (items !== undefined) {
			const replacementCampaignInfo = {}
			items.forEach((master) => {
				replacementCampaignInfo[master.mLocId] = { ...master }
				replacementCampaignInfo[master.mLocId]["replacements"] = {}
				master["replacements"].forEach((replacement) => {
					replacementCampaignInfo[master.mLocId]["replacements"][replacement.index.toString().padStart(3, "0")] =
						replacement.id
				})
			})
			await dispatch({
				type: actionTypes.SET_REPLACEMENT_CAMPAIGN_INFO,
				data: replacementCampaignInfo,
			})
			return "resolved"
		} else {
			await dispatch({
				type: actionTypes.SET_REPLACEMENT_CAMPAIGN_STATUS,
				status: "resolved",
			})
			return "empty"
		}
	} else {
		await dispatch({
			type: actionTypes.SET_REPLACEMENT_CAMPAIGN_STATUS,
			status: "resolved",
		})
		return "empty"
	}
}

export const subscribeReplacementCampaignUpdates = (layoutId) => (dispatch) => {
	const subscription = API.graphql(
		graphqlOperation(subscribeReplacementCampaignUpdateGql, {
			layoutId,
		}),
	).subscribe({
		next: ({ value }) => {
			const { data } = value
			console.log(data)
			if (data && data.subscribeReplacementCampaignUpdate) {
				console.log("subscribeReplacementCampaign", data)
				dispatch({
					type: actionTypes.UPDATE_REPLACEMENT_CAMPAIGN_INFO,
					...data.subscribeReplacementCampaignUpdate,
					nLocId: data.subscribeReplacementCampaignUpdate.nLocId.toString().padStart(3, "0"),
				})
			}
		},
		error: (err) => console.log("SUB ERROR:", err),
	})
	return subscription.unsubscribe.bind(subscription)
}

export const subscribeNodeSyncBitmasksUpdate = (layoutId) => (dispatch) => {
	const subscription = API.graphql(
		graphqlOperation(subscribeNodeSyncBitmasksUpdateGql, {
			layoutId,
		}),
	).subscribe({
		next: async (data) => {
			if (data) {
				console.log("subscribeNodeSyncBitmasksUpdateGql", data)
				const nodeSyncBitmasksUpdate = ((data["value"] || {})["data"] || {})["subscribeNodeSyncBitmasksUpdate"]
				if (nodeSyncBitmasksUpdate) {
					const { mLocId, bitmasks } = nodeSyncBitmasksUpdate
					await dispatch({
						type: actionTypes.UPDATE_NODE_SYNC_BITMASKS,
						mLocId,
						bitmasks,
					})
				}
			}
		},
		error: (err) => console.log("SUB ERROR:", err),
	})
	return subscription.unsubscribe.bind(subscription)
}

export const subscribeNodeIdUpdates = (layoutId) => (dispatch) => {
	const subscription = API.graphql(
		graphqlOperation(subscribeNodeIdUpdateGql, {
			layoutId,
		}),
	).subscribe({
		next: async (data) => {
			if (data) {
				console.log("subscribeNodeIdUpdateGql", data)
				const nodeIdUpdate = ((data["value"] || {})["data"] || {})["subscribeNodeIdUpdate"]
				if (nodeIdUpdate) {
					const { mLocId, nLocId, nId, deltaAction } = nodeIdUpdate
					if (deltaAction === "UPDATE") {
						await dispatch({
							type: actionTypes.UPDATE_NODE_ID,
							mLocId,
							nLocId,
							nId,
							status: "accepted",
						})
					}
				}
			}
		},
		error: (err) => console.log("SUB ERROR:", err),
	})
	return subscription.unsubscribe.bind(subscription)
}

export const subscribeMasterIdUpdates = (layoutId) => (dispatch) => {
	const subscription = API.graphql(
		graphqlOperation(subscribeMasterIdUpdateGql, {
			layoutId,
		}),
	).subscribe({
		next: async (data) => {
			if (data) {
				console.log("subscribeMasterIdUpdateGql", data)
				const masterIdUpdate = ((data["value"] || {})["data"] || {})["subscribeMasterIdUpdate"]
				if (masterIdUpdate) {
					const { mLocId, mId, deltaAction } = masterIdUpdate
					if (deltaAction === "UPDATE") {
						const masterDetails = await (await offlineStorage).get("masterDetails", mLocId)
						await (await offlineStorage).put("masterDetails", { ...masterDetails, mId })
						console.log("subscribeMasterIdUpdates", masterDetails)
						await dispatch({
							type: actionTypes.UPDATE_MASTER_ID,
							mLocId,
							mId,
							status: "accepted",
						})
					}
				}
			}
		},
		error: (err) => console.log("SUB ERROR:", err),
	})
	return subscription.unsubscribe.bind(subscription)
}

export const subscribeOnlineStatus = () => (dispatch) => {
	const updateOnlineStatus = () => {
		dispatch({
			type: actionTypes.ONLINE_STATUS,
			status: navigator.onLine ? "online" : "offline",
		})
	}

	updateOnlineStatus()

	window.addEventListener("online", updateOnlineStatus)
	window.addEventListener("offline", updateOnlineStatus)
	return () => {
		window.removeEventListener("online", updateOnlineStatus)
		window.removeEventListener("offline", updateOnlineStatus)
	}
}
